import React, { useMemo, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { CaretDownIcon, CaretLeftIcon, SearchIcon } from '@modernloop/shared/icons';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Theme,
  Typography,
} from '@mui/material';

import OrganizationSwitcherButton from 'src/components/OrganizationSwitcher/OrganizationSwitcherButton';

import useExitSettings from 'src/hooks/useExitSettings';
import useHistory from 'src/hooks/useHistory';
import useIsNewOrgExperience from 'src/hooks/useIsNewOrgExperience';

import { useGetOrgSettingsNavItems } from './useGetOrgSettingsNavItems';

const matchesSearchQuery = (label: string, normalizedSearchQuery: string) => {
  return label.toLocaleLowerCase().includes(normalizedSearchQuery);
};

/**
 * Renders the organization settings sidebar with collapsible section groups and direct page links.
 */
export const SettingsNav = () => {
  const { t } = useTranslation();
  const navItems = useGetOrgSettingsNavItems();
  const exitSettings = useExitSettings();
  const history = useHistory();
  const [collapsedSections, setCollapsedSections] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const isNewOrgExperience = useIsNewOrgExperience();

  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();

  const filteredNavItems = useMemo(() => {
    if (!normalizedSearchQuery) {
      return navItems;
    }

    return navItems
      .map((navItem) => {
        const matchingNavs = navItem.navs.filter(
          (nav) => nav.isShown && matchesSearchQuery(nav.label, normalizedSearchQuery)
        );
        const sectionMatches = matchesSearchQuery(navItem.label, normalizedSearchQuery);

        if (!sectionMatches && matchingNavs.length === 0) {
          return null;
        }

        return {
          ...navItem,
          navs: sectionMatches ? navItem.navs.filter((nav) => nav.isShown) : matchingNavs,
        };
      })
      .filter((navItem): navItem is NonNullable<typeof navItem> => navItem !== null);
  }, [navItems, normalizedSearchQuery]);

  return (
    <Stack
      minWidth="240px"
      height="100%"
      sx={{
        backgroundColor: (theme: Theme) => theme.palette.background.contrast,
        flexShrink: 0,
        flexWrap: 'nowrap',
        height: '100%',
        overflowX: 'hidden',
      }}
    >
      <Stack
        direction="row"
        gap={0.5}
        alignItems="center"
        padding={(theme: Theme) => {
          return `${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(2)}`;
        }}
      >
        {!isNewOrgExperience && (
          <IconButton onClick={exitSettings}>
            <CaretLeftIcon />
          </IconButton>
        )}
        <Typography variant="subtitle1">{t('hiring-dashboard-nav:nav.settings.title')}</Typography>
      </Stack>
      {isNewOrgExperience && (
        /*
         * The onboarding shell has no account menu, so this is the only Organization switcher a
         * user sees while the Organization they switched into holds no directory record for them.
         * Without it they could switch in and never switch back out.
         */
        <Stack px={1} pb={1}>
          <OrganizationSwitcherButton />
        </Stack>
      )}
      <Stack px={1} pb={1}>
        <TextField
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t('hiring-dashboard-nav:nav.settings.searchPlaceholder')}
          InputProps={{
            startAdornment: <SearchIcon />,
          }}
        />
      </Stack>
      {filteredNavItems.map((navItem) => {
        if (!navItem.isShown) {
          return null;
        }
        const isSectionExpanded = navItem.hideSectionLabel
          ? true
          : normalizedSearchQuery
            ? true
            : !collapsedSections.includes(navItem.id);

        return (
          <Stack key={navItem.id}>
            <List>
              <Stack px={0.5}>
                {!navItem.hideSectionLabel ? (
                  <ListSubheader component="div" disableSticky sx={{ px: 0, backgroundColor: 'transparent' }}>
                    <ListItemButton
                      dense
                      onClick={() => {
                        setCollapsedSections((sectionIds) => {
                          if (sectionIds.includes(navItem.id)) {
                            return sectionIds.filter((sectionId) => sectionId !== navItem.id);
                          }
                          return [...sectionIds, navItem.id];
                        });
                      }}
                      sx={{ px: 1.5, py: 0.5, borderRadius: 1 }}
                    >
                      <ListItemText
                        primary={navItem.label}
                        primaryTypographyProps={{ variant: 'caption', fontWeight: 600, color: 'text.secondary' }}
                      />
                      <ListItemIcon
                        sx={{
                          minWidth: 24,
                          color: (theme: Theme) => theme.palette.text.secondary,
                          transform: isSectionExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                          transition: (theme: Theme) => theme.transitions.create('transform'),
                        }}
                      >
                        <CaretDownIcon />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListSubheader>
                ) : null}
                <Collapse in={isSectionExpanded}>
                  {navItem.navs.map((nav) => {
                    if (!nav.isShown) {
                      return null;
                    }
                    return (
                      <Stack key={nav.id}>
                        <ListItemButton
                          onClick={() => {
                            history.replace(nav.to);
                          }}
                          key={nav.id}
                          dense
                          selected={nav.isActive}
                          sx={
                            nav.isActive
                              ? {
                                  backgroundColor: (theme: Theme) => `${theme.palette.primary.main} !important`,
                                }
                              : {}
                          }
                        >
                          <ListItemIcon
                            sx={
                              nav.isActive
                                ? {
                                    color: (theme: Theme) => theme.palette.common.white,
                                  }
                                : { color: (theme: Theme) => theme.palette.text.primary }
                            }
                          >
                            {nav.icon}
                          </ListItemIcon>
                          <ListItemText
                            sx={
                              nav.isActive
                                ? {
                                    color: (theme: Theme) => theme.palette.common.white,
                                  }
                                : {}
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <span>{nav.label}</span>
                              {nav.labelAdornment ? (
                                <Box
                                  sx={{
                                    color: nav.isActive ? 'common.white' : 'primary.dark',
                                    display: 'flex',
                                    flexShrink: 0,
                                  }}
                                >
                                  {nav.labelAdornment}
                                </Box>
                              ) : null}
                            </Stack>
                          </ListItemText>
                        </ListItemButton>
                        {nav.showDividerAfter ? (
                          <Divider
                            sx={{
                              my: 1.5,
                            }}
                          />
                        ) : null}
                      </Stack>
                    );
                  })}
                </Collapse>
              </Stack>
            </List>
            {navItem.showDivider && (
              <Divider
                sx={{
                  my: 1.5,
                }}
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
